import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMemberships, getSession, type MembershipsResponse, type SessionClaims } from './api.js';
import { signInPathFor } from './return-path.js';

export type SessionState =
  | { status: 'loading' }
  | { status: 'authed'; claims: SessionClaims; memberships: MembershipsResponse | null };

/**
 * Load the current session (and the tenant-scoped memberships used as the RLS proof). Redirects to
 * /sign-in when unauthenticated, so any authed page can call this and trust an 'authed' result.
 *
 * ⚠️ THE REDIRECT CARRIES WHERE THEY WERE GOING (#968). This is the cold-session path a shared link
 * lands on, and it used to drop the destination silently: a colleague sent a document link, opened it
 * on a lapsed session, and arrived at the project list with nothing saying a link had been lost.
 */
export function useSession(): SessionState {
  const navigate = useNavigate();
  const [state, setState] = useState<SessionState>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    // CAPTURED BEFORE THE AWAIT, deliberately. Read after `getSession()` resolves, this would be
    // whatever the address bar says by then — and if two components ever mount `useSession`, the
    // second resolver would read `/sign-in?next=…` (React Router mutates history synchronously),
    // `signInPathFor` would refuse its own `/sign-in` prefix, and the redirect would come out BARE,
    // silently clobbering the capture. Reading once, up front, cannot do that.
    const attempted = signInPathFor(window.location, window.location.origin);
    void (async () => {
      const claims = await getSession();
      if (!active) return;
      if (!claims) {
        // `window.location` rather than `useLocation()`: both SPAs mount a BrowserRouter, so the two
        // agree, and this keeps the effect keyed on identity alone. Taking the router's location as a
        // dependency would re-run the whole session load on every in-app navigation.
        void navigate(attempted, { replace: true });
        return;
      }
      const memberships = await getMemberships();
      if (!active) return;
      setState({ status: 'authed', claims, memberships });
    })();
    return () => {
      active = false;
    };
  }, [navigate]);

  return state;
}
